// pages/shop/[id]/[slug].tsx
import React, { useMemo } from "react";
import Head from "next/head";
import SEO from "components/seo";
import StoreContainer from "containers/storeContainer/storeContainer";
import ShopHeader from "containers/shopHeader/shopHeader";
// import BrandList from "containers/brandList/brandList";
import type { GetServerSideProps } from "next";
import { useQuery } from "react-query";
import shopService from "services/shop";
import { useRouter } from "next/router";
import productService from "services/product";
import { useAppSelector } from "hooks/useRedux";
import { selectCurrency } from "redux/slices/currency";
import { useTranslation } from "react-i18next";
import dynamic from "next/dynamic";
import { getCookie, removeCookie } from "utils/session";
import getImage from "utils/getImage";
// import brandService from "services/brand";
import ShopProducts from "components/shop-products/shop-products";
import { useMediaQuery } from "@mui/material";

const ShopHorizontalCategories = dynamic(
  () => import("components/shopHorizontalCategories/shopHorizontalCategories")
);
const PageLoading = dynamic(() => import("components/loader/pageLoading"));

type Props = {
  memberState: any;
  shopData: any;          // dati del negozio arrivati da SSR
  canonicalUrl: string;   // URL canonica dello shop
};

// 🔤 funzione per creare lo slug "compatto"
const compactSlug = (s: string): string =>
  (s || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

// ✅ slug valido: solo lettere/numeri e almeno una lettera
const isValidSlug = (s: string): boolean =>
  /^[a-z0-9]+$/.test(s) && /[a-z]/.test(s);

export default function ShopSingle({
  memberState,
  shopData,
  canonicalUrl,
}: Props) {
  const isDesktop = useMediaQuery("(min-width: 1140px)");
  const { i18n, t } = useTranslation();
  const locale = i18n.language;
  const { query } = useRouter();
  const shopId = Number(query.id);
  const currency = useAppSelector(selectCurrency);

  // 👉 per lo shop NON usiamo più useQuery: abbiamo già shopData da SSR

  // prodotti del negozio
  const {
    data: products,
    isLoading: isProductsLoading,
  } = useQuery({
    queryKey: ["products", shopId, currency?.id, locale],
    queryFn: () => {
      const params = { currency_id: currency?.id };
      return productService.getAllShopProducts(shopId, params);
    },
    staleTime: 0,
    enabled: !!shopId,
  });

  const extractedCategories = useMemo(() => {
    const categories =
      products?.data?.all?.reduce((acc: any, item: any) => {
        if (Array.isArray(item?.products) && item?.products?.length) {
          acc.push(item);
        }
        if (Array.isArray(item?.children) && item?.children?.length) {
          acc.push(...item?.children);
        }
        return acc;
      }, []) || [];

    if (products?.data?.recommended?.length) {
      return [
        { uuid: "popular", translation: { title: t("popular") }, id: 0 },
        ...categories,
      ];
    }

    return categories;
  }, [products?.data, t]);

  // --- SEO & JSON-LD -------------------------------------------------------

  const shop: any = shopData || {};
  const shopTitle =
    shop?.translation?.title ??
    shop?.name ??
    "Gloco";

  const shopDescription =
    shop?.translation?.description ??
    "Il 1° Marketplace digitale locale, scopri i Negozi della tua città";

  const shopImage = getImage(shop?.logo_img) || "";

  // 🔎 proviamo a ricavare una stringa di indirizzo dal negozio
  const shopAddressText =
    shop?.address_text ??
    shop?.address ??
    shop?.location ??
    "";

  // JSON-LD LocalBusiness (base, ora con indirizzo se disponibile)
  const jsonLd: any = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: shopTitle,
    description: shopDescription,
    url: canonicalUrl,
  };

  if (shopImage) {
    jsonLd.image = shopImage;
  }
  if (shop?.phone) {
    jsonLd.telephone = shop.phone;
  }

  // ✅ aggiunta ADDRESS: viene inserito solo se abbiamo almeno una stringa
  if (shopAddressText) {
    jsonLd.address = {
      "@type": "PostalAddress",
      streetAddress: shopAddressText,
      addressCountry: "IT",
    };
  }

  // ------------------------------------------------------------------------

  // se per qualche motivo non abbiamo i dati shop, mostriamo loader
  if (!shop || !shop.id) {
    return <PageLoading />;
  }

  return (
    <>
      {/* Meta generici (title, description, og:image, ecc.) */}
      <SEO title={shopTitle} description={shopDescription} image={shopImage} />

      {/* Canonical + JSON-LD LocalBusiness */}
      <Head>
        <link rel="canonical" href={canonicalUrl} />
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      </Head>

      <StoreContainer
        data={shop}
        memberState={memberState}
        categories={extractedCategories}
      >
        <ShopHeader loading={false} />
        {!isDesktop && (
          <ShopHorizontalCategories categories={extractedCategories} />
        )}
        <ShopProducts products={products?.data} isLoading={isProductsLoading} />
        {/* <BrandList brands={brands?.data} isLoading={isBrandsLoading} /> */}
      </StoreContainer>
    </>
  );
}

// 🔁 SSR: controlla slug, carica lo shop e passa tutto come props
export const getServerSideProps: GetServerSideProps<Props> = async (ctx) => {
  const id = String(ctx.params?.id || "");
  const incomingSlug = String(ctx.params?.slug || "");

  // gestione memberState (come prima)
  const groupId = Number(ctx.query.g);
  let memberState = getCookie("member", ctx);
  if (memberState && groupId) {
    if (memberState.cart_id !== groupId) {
      removeCookie("member");
      memberState = null;
    }
  }

  // slug base valido?
  if (!isValidSlug(incomingSlug)) {
    return { notFound: true };
  }

  // recupero shop dal backend
  let shopData: any;
  try {
    const res = await shopService.getById(Number(id));
    shopData = (res as any)?.data ?? res;
  } catch (e) {
    return { notFound: true };
  }

  if (!shopData) {
    return { notFound: true };
  }

  // calcolo slug canonico dal titolo del negozio
  const titleForSlug: string =
    shopData?.translation?.title ??
    shopData?.name ??
    "";

  const canonicalSlug = compactSlug(titleForSlug);

  // se lo slug dell’URL non coincide → 404
  if (incomingSlug !== canonicalSlug) {
    return { notFound: true };
  }

  // URL canonica assoluta
  const baseUrl =
    process.env.NEXT_PUBLIC_SITE_URL || "https://www.gloco.it";
  const canonicalUrl = `${baseUrl}/shop/${id}/${canonicalSlug}`;

  return {
    props: {
      memberState,
      shopData,
      canonicalUrl,
    },
  };
};
